import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from "firebase/firestore";
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { db, storage } from "../../firebase/config";

const SUBSCRIPTIONS_COLLECTION = "subscriptions";

// Async thunks for Firebase operations

// Add a new subscription
export const addSubscription = createAsyncThunk(
  "subscriptions/addSubscription",
  async ({ subscriptionData, invoiceFile }, { rejectWithValue }) => {
    try {
      console.log('Adding subscription:', subscriptionData);
      
      let invoiceFileUrl = null;
      let invoiceFileName = null;
      
      // Upload invoice file if provided
      if (invoiceFile) {
        try {
          const fileExtension = invoiceFile.name.split('.').pop();
          const fileName = `invoice_${Date.now()}.${fileExtension}`;
          const storageRef = ref(storage, `subscriptions/invoices/${fileName}`);
          
          console.log('Uploading invoice file:', fileName);
          const uploadResult = await uploadBytes(storageRef, invoiceFile);
          invoiceFileUrl = await getDownloadURL(uploadResult.ref);
          invoiceFileName = invoiceFile.name;
          
          console.log('Invoice file uploaded successfully:', invoiceFileUrl);
        } catch (uploadError) {
          console.error('Error uploading invoice file:', uploadError);
          // Continue without file if upload fails
        }
      }
      
      const docData = {
        ...subscriptionData,
        invoiceFileUrl,
        invoiceFileName,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(collection(db, SUBSCRIPTIONS_COLLECTION), docData);
      console.log('Subscription added successfully with ID:', docRef.id);
      
      return { 
        id: docRef.id, 
        ...docData 
      };
    } catch (error) {
      console.error('Error adding subscription:', error);
      return rejectWithValue(
        error.message || "Failed to add subscription"
      );
    }
  }
);

// Fetch user's subscriptions
export const fetchUserSubscriptions = createAsyncThunk(
  "subscriptions/fetchUserSubscriptions",
  async (userEmail, { rejectWithValue }) => {
    try {
      console.log('Fetching subscriptions for user:', userEmail);
      
      const subscriptionsRef = collection(db, SUBSCRIPTIONS_COLLECTION);
      const q = query(
        subscriptionsRef, 
        where("userEmail", "==", userEmail),
        orderBy("createdAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      const subscriptions = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      console.log('Fetched user subscriptions:', subscriptions);
      return subscriptions;
    } catch (error) {
      console.error("Error fetching user subscriptions:", error);
      return rejectWithValue(
        error.message || "Failed to fetch subscriptions"
      );
    }
  }
);

// Fetch all subscriptions (admin only)
export const fetchAllSubscriptions = createAsyncThunk(
  "subscriptions/fetchAllSubscriptions",
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching all subscriptions');
      
      const subscriptionsRef = collection(db, SUBSCRIPTIONS_COLLECTION);
      const q = query(subscriptionsRef, orderBy("createdAt", "desc"));
      
      const snapshot = await getDocs(q);
      const subscriptions = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      console.log('Fetched all subscriptions:', subscriptions);
      return subscriptions;
    } catch (error) {
      console.error("Error fetching all subscriptions:", error);
      return rejectWithValue(
        error.message || "Failed to fetch all subscriptions"
      );
    }
  }
);

// Update a subscription
export const updateSubscription = createAsyncThunk(
  "subscriptions/updateSubscription",
  async ({ subscriptionId, updateData, invoiceFile, existingInvoiceUrl }, { rejectWithValue }) => {
    try {
      console.log('Updating subscription:', subscriptionId, updateData);
      
      let invoiceFileUrl = existingInvoiceUrl;
      let invoiceFileName = updateData.invoiceFileName;
      
      // Upload new invoice file if provided
      if (invoiceFile) {
        try {
          // Delete existing file if there was one
          if (existingInvoiceUrl) {
            try {
              const existingFileRef = ref(storage, existingInvoiceUrl);
              await deleteObject(existingFileRef);
              console.log('Existing invoice file deleted');
            } catch (deleteError) {
              console.log('Could not delete existing file:', deleteError);
            }
          }
          
          const fileExtension = invoiceFile.name.split('.').pop();
          const fileName = `invoice_${Date.now()}.${fileExtension}`;
          const storageRef = ref(storage, `subscriptions/invoices/${fileName}`);
          
          console.log('Uploading new invoice file:', fileName);
          const uploadResult = await uploadBytes(storageRef, invoiceFile);
          invoiceFileUrl = await getDownloadURL(uploadResult.ref);
          invoiceFileName = invoiceFile.name;
          
          console.log('New invoice file uploaded successfully:', invoiceFileUrl);
        } catch (uploadError) {
          console.error('Error uploading new invoice file:', uploadError);
          // Continue with existing file if upload fails
        }
      }
      
      const subscriptionRef = doc(db, SUBSCRIPTIONS_COLLECTION, subscriptionId);
      const docData = {
        ...updateData,
        invoiceFileUrl,
        invoiceFileName,
        updatedAt: Timestamp.now()
      };
      
      await updateDoc(subscriptionRef, docData);
      console.log('Subscription updated successfully');
      
      return { 
        id: subscriptionId, 
        ...docData 
      };
    } catch (error) {
      console.error('Error updating subscription:', error);
      return rejectWithValue(
        error.message || "Failed to update subscription"
      );
    }
  }
);

// Delete a subscription
export const deleteSubscription = createAsyncThunk(
  "subscriptions/deleteSubscription",
  async ({ subscriptionId, invoiceFileUrl }, { rejectWithValue }) => {
    try {
      console.log('Deleting subscription:', subscriptionId);
      
      // Delete associated invoice file if it exists
      if (invoiceFileUrl) {
        try {
          const fileRef = ref(storage, invoiceFileUrl);
          await deleteObject(fileRef);
          console.log('Invoice file deleted successfully');
        } catch (fileError) {
          console.log('Could not delete invoice file:', fileError);
          // Continue with subscription deletion even if file deletion fails
        }
      }
      
      const subscriptionRef = doc(db, SUBSCRIPTIONS_COLLECTION, subscriptionId);
      await deleteDoc(subscriptionRef);
      
      console.log('Subscription deleted successfully');
      return subscriptionId;
    } catch (error) {
      console.error('Error deleting subscription:', error);
      return rejectWithValue(
        error.message || "Failed to delete subscription"
      );
    }
  }
);

const subscriptionSlice = createSlice({
  name: "subscriptions",
  initialState: {
    userSubscriptions: [],
    allSubscriptions: [],
    loading: false,
    error: null,
    isAdding: false,
    isUpdating: false,
    isDeleting: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSubscriptions: (state) => {
      state.userSubscriptions = [];
      state.allSubscriptions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Add subscription
      .addCase(addSubscription.pending, (state) => {
        state.isAdding = true;
        state.error = null;
      })
      .addCase(addSubscription.fulfilled, (state, action) => {
        state.isAdding = false;
        state.userSubscriptions.unshift(action.payload);
        state.allSubscriptions.unshift(action.payload);
      })
      .addCase(addSubscription.rejected, (state, action) => {
        state.isAdding = false;
        state.error = action.payload;
      })
      
      // Fetch user subscriptions
      .addCase(fetchUserSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.userSubscriptions = action.payload;
      })
      .addCase(fetchUserSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch all subscriptions
      .addCase(fetchAllSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.allSubscriptions = action.payload;
      })
      .addCase(fetchAllSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update subscription
      .addCase(updateSubscription.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.isUpdating = false;
        const { id, ...updateData } = action.payload;
        
        // Update in userSubscriptions
        const userIndex = state.userSubscriptions.findIndex(sub => sub.id === id);
        if (userIndex !== -1) {
          state.userSubscriptions[userIndex] = { 
            ...state.userSubscriptions[userIndex], 
            ...updateData 
          };
        }
        
        // Update in allSubscriptions
        const allIndex = state.allSubscriptions.findIndex(sub => sub.id === id);
        if (allIndex !== -1) {
          state.allSubscriptions[allIndex] = { 
            ...state.allSubscriptions[allIndex], 
            ...updateData 
          };
        }
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      
      // Delete subscription
      .addCase(deleteSubscription.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteSubscription.fulfilled, (state, action) => {
        state.isDeleting = false;
        const subscriptionId = action.payload;
        
        // Remove from userSubscriptions
        state.userSubscriptions = state.userSubscriptions.filter(
          sub => sub.id !== subscriptionId
        );
        
        // Remove from allSubscriptions
        state.allSubscriptions = state.allSubscriptions.filter(
          sub => sub.id !== subscriptionId
        );
      })
      .addCase(deleteSubscription.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSubscriptions } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;